import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';

const PrivacyWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const LastUpdated = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.spacing['2xl']};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const SubsectionTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Paragraph = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.md};
`;

const List = styled.ul`
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.md};
  padding-left: ${theme.spacing.xl};
`;

const ListItem = styled.li`
  margin-bottom: ${theme.spacing.sm};
`;

const HighlightBox = styled.div`
  background-color: ${theme.colors.primaryLight}20;
  border-left: 4px solid ${theme.colors.primary};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.xl} 0;
  border-radius: ${theme.radii.md};
`;

export const PrivacyPage: React.FC = () => {
  return (
    <PrivacyWrapper>
      <Container>
        <Content>
          <Title>Privacy Policy</Title>
          <LastUpdated>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

          <HighlightBox>
            <Paragraph style={{ margin: 0, color: theme.colors.text.primary }}>
              <strong>Your Privacy Matters:</strong> Promptasaurus is designed with privacy-first principles. 
              We process prompts locally when possible, minimize data collection, and never sell your information.
            </Paragraph>
          </HighlightBox>

          <Section>
            <SectionTitle>1. Introduction</SectionTitle>
            <Paragraph>
              Promptasaurus ("we," "our," or "us"), a project by Gruntwork.ai, is committed to protecting 
              your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our website, browser extension, and services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Information We Collect</SectionTitle>
            
            <SubsectionTitle>Information You Provide</SubsectionTitle>
            <List>
              <ListItem>
                <strong>Account Information:</strong> Email address, username, and password when you create an account
              </ListItem>
              <ListItem>
                <strong>Prompts and Templates:</strong> Content you create, save, or improve using our services
              </ListItem>
              <ListItem>
                <strong>Contact Information:</strong> Name and email when you contact us for support
              </ListItem>
              <ListItem>
                <strong>Preferences:</strong> Settings and preferences you configure in our applications
              </ListItem>
            </List>

            <SubsectionTitle>Information Collected Automatically</SubsectionTitle>
            <List>
              <ListItem>
                <strong>Usage Data:</strong> Features used, templates accessed, and general interaction patterns
              </ListItem>
              <ListItem>
                <strong>Technical Data:</strong> Browser type, operating system, and device information
              </ListItem>
              <ListItem>
                <strong>Analytics:</strong> Anonymous usage statistics to improve our services
              </ListItem>
            </List>

            <SubsectionTitle>Information We DON'T Collect</SubsectionTitle>
            <List>
              <ListItem>We do not collect or store your API keys for third-party services</ListItem>
              <ListItem>We do not track your activities on ChatGPT, Claude, or other AI platforms</ListItem>
              <ListItem>We do not collect personal data from the browser extension unless you're logged in</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. How We Use Your Information</SectionTitle>
            <Paragraph>We use the information we collect to:</Paragraph>
            <List>
              <ListItem>Provide and maintain our services</ListItem>
              <ListItem>Process and improve your prompts (when using cloud features)</ListItem>
              <ListItem>Save and sync your templates and preferences</ListItem>
              <ListItem>Send important service updates and notifications</ListItem>
              <ListItem>Respond to your support requests</ListItem>
              <ListItem>Improve our products and develop new features</ListItem>
              <ListItem>Ensure security and prevent fraud</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Data Processing and Storage</SectionTitle>
            
            <SubsectionTitle>Local Processing</SubsectionTitle>
            <Paragraph>
              Whenever possible, Promptasaurus processes your prompts locally in your browser. 
              This means your data never leaves your device for basic prompt improvements.
            </Paragraph>

            <SubsectionTitle>Cloud Processing</SubsectionTitle>
            <Paragraph>
              When you use advanced features or opt into cloud processing:
            </Paragraph>
            <List>
              <ListItem>Your prompts are sent to our secure servers for processing</ListItem>
              <ListItem>We use industry-standard encryption in transit (HTTPS/TLS)</ListItem>
              <ListItem>Processed data is immediately returned and not stored unless you save it</ListItem>
              <ListItem>Saved templates and preferences are encrypted at rest</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>5. Data Sharing and Disclosure</SectionTitle>
            <Paragraph>
              We do not sell, trade, or rent your personal information. We may share information only in these circumstances:
            </Paragraph>
            <List>
              <ListItem>
                <strong>Service Providers:</strong> With trusted third parties who help us operate our services 
                (e.g., hosting providers, email services)
              </ListItem>
              <ListItem>
                <strong>Legal Requirements:</strong> If required by law or to protect our rights and safety
              </ListItem>
              <ListItem>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets
              </ListItem>
              <ListItem>
                <strong>With Your Consent:</strong> When you explicitly authorize us to share information
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>6. Data Security</SectionTitle>
            <Paragraph>
              We implement appropriate technical and organizational measures to protect your information:
            </Paragraph>
            <List>
              <ListItem>Encryption of data in transit and at rest</ListItem>
              <ListItem>Regular security audits and updates</ListItem>
              <ListItem>Access controls and authentication</ListItem>
              <ListItem>Secure coding practices and regular vulnerability testing</ListItem>
            </List>
            <Paragraph>
              However, no method of transmission over the Internet is 100% secure. While we strive to 
              protect your information, we cannot guarantee absolute security.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. Your Rights and Choices</SectionTitle>
            <Paragraph>You have the right to:</Paragraph>
            <List>
              <ListItem>Access, update, or delete your account information</ListItem>
              <ListItem>Export your saved templates and data</ListItem>
              <ListItem>Opt-out of marketing communications</ListItem>
              <ListItem>Request deletion of your personal data</ListItem>
              <ListItem>Disable analytics and tracking in the extension settings</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. Cookies and Tracking</SectionTitle>
            <Paragraph>
              We use minimal cookies and similar technologies:
            </Paragraph>
            <List>
              <ListItem>
                <strong>Essential Cookies:</strong> Required for authentication and basic functionality
              </ListItem>
              <ListItem>
                <strong>Analytics Cookies:</strong> To understand usage patterns (can be disabled)
              </ListItem>
              <ListItem>
                <strong>No Third-Party Tracking:</strong> We don't use advertising or social media tracking cookies
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>9. Children's Privacy</SectionTitle>
            <Paragraph>
              Promptasaurus is not directed to individuals under the age of 13. We do not knowingly 
              collect personal information from children under 13. If you believe we have collected 
              information from a child under 13, please contact us immediately.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>10. International Data Transfers</SectionTitle>
            <Paragraph>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your information in accordance 
              with this Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. Changes to This Policy</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. 
              For significant changes, we will provide additional notice via email or the application.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. Contact Us</SectionTitle>
            <Paragraph>
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </Paragraph>
            <List>
              <ListItem>Email: privacy@gruntwork.ai</ListItem>
              <ListItem>Website: https://promptasaurus.ai/contact</ListItem>
              <ListItem>GitHub: https://github.com/gruntwork/promptasaurus</ListItem>
            </List>
          </Section>

          <HighlightBox>
            <Paragraph style={{ margin: 0 }}>
              <strong>Data Portability:</strong> We believe your data belongs to you. You can export your 
              templates and settings at any time, and request deletion of your account and all associated data.
            </Paragraph>
          </HighlightBox>
        </Content>
      </Container>
    </PrivacyWrapper>
  );
};